<script setup lang="ts">
import type { ReportAreaDetailProps } from '@/types/appTypes'

const props = withDefaults(defineProps<ReportAreaDetailProps>(), {
  title: '',
  objects: () => [],
})

const app = useNuxtApp()
const storeIndex = useStoreIndex()

let sBody = null // ボディ
let sReporterImg = null // 読者レポーター画像
let sPlace = null

// 関連記事からイベント取得
const eventItem = app.$getArticleRelationsSingle(props.item, 'event', null)
if (eventItem) {
  // イベントから表示カラム作成
  if (!sPlace) {
    sPlace = app.$getEventPlace(eventItem)
  } // 場所
}

// レポートから表示カラム作成
sBody = app.$getArticleBody(props.item) // ボディ

// ロゴ画像取得
if (props.item && props.item.author_object && props.objects) {
  sReporterImg = storeIndex.getImgInfo(props.item.author_object.icon_object, 's')?.url
}
</script>

<template>
  <div>
    <div class="l-label">
      <div class="l-label_content">
        <div
          class="l-label_content_avatar"
        >
          読者<br>レポート
        </div>
        <div class="l-label_content_header">
          <div
            v-if="item.title"
            class="l-label_content_header_title"
          >
            {{ item.title }}
          </div>
          <div
            v-if="sPlace"
            class="l-label_content_header_location"
          >
            {{ sPlace }}
          </div>
        </div>
      </div>
      <v-divider />
      <div
        v-if="item.open_date"
        class="l-label_content_header_footer"
      >
        {{ app.$getPeriodDayLabelNoDD(item.open_date) }}
      </div>
    </div>

    <div class="l-body">
      <div
        v-if="sBody"
        class="l-body_text l-blank-icon-none"
        v-html="sBody"
      />
      <div
        v-if="app.$isArticleOld(item)"
        class="mb-4"
      >
        <ReportAreaInfo />
      </div>
    </div>

    <ReportInfo
      v-bind:item="item"
    />

    <div class="c-link">
      <Clip
        v-bind:mode="true"
        v-bind:dspcnt="true"
        v-bind:article-type-slug="item.article_type_slug"
        v-bind:article-slug="item.article_slug"
        v-bind:count="item.user_favorite_count"
      />
      <ShareButtons
        v-bind:title="title"
      />
    </div>

    <div
      v-if="!app.$isArticleOld(item)"
      class="l-reporter"
    >
      <div class="l-reporter_ui">
        <div class="l-reporter_ui_header">
          <div class="l-reporter_ui_header_title">
            <v-icon class="l-reporter_ui_header_icon">
              mdi-square
            </v-icon>
            読者レポーターのご紹介
          </div>
        </div>
        <div
          v-if="item.author_object && (sReporterImg || item.author_object.name || item.author_object.personal_introduction)"
          class="l-reporter_ui_content"
        >
          <div
            v-if="sReporterImg"
            class="l-reporter_ui_content_imageBox"
          >
            <CommonImage
              v-bind:src="sReporterImg"
              class="l-reporter_ui_content_imageBox_image"
              error-src="/images/noimage.svg"
            />
          </div>
          <div
            v-if="item.author_object.name || item.author_object.personal_introduction"
            class="l-reporter_ui_content_info"
          >
            <div
              v-if="item.author_object.name"
              class="l-reporter_ui_content_info_name"
            >
              {{ item.author_object.name }}
            </div>
            <div
              v-if="item.author_object.personal_introduction"
              class="l-reporter_ui_content_info_detail"
            >
              <div v-html="item.author_object.personal_introduction" />
            </div>
          </div>
        </div>
      </div>
      <ReportAreaInfo />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use "@/assets/css/foundation/mixin/responsive";

.l-label {
  width: 100%;
  margin-top: 10px;

  @include responsive.mq_down(sp) {
    margin-top: 0;
  }

  &_content {
    display: flex;
    margin-bottom: 26px;

    @include responsive.mq_down(sp) {
      margin: 0 0 26px;
    }

    &_avatar {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100px;
      min-width: 100px;
      max-width: 100px;
      height: 100px;
      padding: 29px 16px;
      background-color: #009197;
      color: var(--g_color_text_white);
      font-family: var(--g_font_family_NotoSansCJKjp);
      font-size: 16px;
      font-weight: bold;
      text-align: center;

      @include responsive.mq_down(sp) {
        width: 80px;
        min-width: 80px;
        max-width: 80px;
        height: 80px;
        padding: 20px 10px;
        font-size: 14px;
      }
    }

    &_header {
      flex-direction: column;
      margin: 0 0 4px 20px;

      @include responsive.mq_down(sp) {
        flex: 1;
        margin: 2px 0 4px 10px;
      }

      &_title {
        margin-top: 2px;
        color: var(--color_text_base);
        font-family: var(--g_font_family_NotoSansCJKjp);
        font-size: 26px;
        line-height: 1.2;

        @include responsive.mq_down(sp) {
          margin: 0 0 10px;
          font-size: 20px;
          line-height: 1.4;
        }
      }

      &_location {
        margin-top: 17px;
        margin-left: 1px;
        color: var(--color_text_base);
        font-family: var(--g_font_family_NotoSansCJKjp);
        font-size: 14px;
        font-weight: bold;

        @include responsive.mq_down(sp) {
          margin: 0;
        }
      }

      &_footer {
        display: flex;
        justify-content: flex-end;
        margin-top: 15px;
        color: var(--g_color_text_626262);
        font-family: var(--g_font_family_NotoSansCJKjp);
        font-size: 13px;
      }
    }
  }
}

.l-body {
  width: 100%;
  margin-top: 30px;

  &_text {
    font-family: var(--g_font_family_NotoSansCJKjp);
    font-size: 16px;
    line-height: 1.8;

    @include responsive.mq_down(sp) {
      :deep(div) {
        max-width: 100% !important;
      }

      :deep(img) {
        max-width: 100% !important;
      }
    }
  }
}

.c-link {
  display: flex;
  justify-content: center;
  margin-top: 35px;

  @include responsive.mq_down(sp) {
    flex-direction: column;
    align-items: center;
  }
}

.l-reporter {
  width: 100%;
  margin-top: 85px;

  &_ui {
    padding: 15px 20px 20px;
    border: 1px solid var(--g_color_border_gray);

    &_header {
      &_icon {
        margin-top: -5px;
        color: var(--g_color_icon_black);
        font-size: 10px;
      }

      &_title {
        color: var(--color_text_base);
        font-family: var(--g_font_family_SawarabiGothic);
        font-size: 16px;
      }
    }

    &_content {
      display: flex;
      margin-top: 15px;

      @include responsive.mq_down(sp) {
        display: block;
      }

      &_imageBox {
        &_image {
          min-width: 80px;
          max-width: 80px;
          height: 80px;
          object-fit: contain;
        }
      }

      &_info {
        margin: 2px 15px 0;

        @include responsive.mq_down(sp) {
          margin: 10px 0 0;
        }

        &_name {
          color: var(--color_text_base);
          font-family: var(--g_font_family_NotoSansCJKjp);
          font-size: 16px;
          font-weight: bold;
        }

        &_detail {
          margin-top: 2px;
          color: var(--color_text_base);
          font-family: var(--g_font_family_NotoSansCJKjp);
          font-size: 14px;
        }
      }
    }
  }
}
</style>
